import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { format } from "date-fns"
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Edit,
  Mail,
  Phone,
  Ship,
  Trash2,
  User,
  Users,
  Utensils,
} from "lucide-react"
import { useState } from "react"
import { BookingsService } from "@/client"
import type { BookingDetailResponse } from "@/client/types.gen"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import useCustomToast from "@/hooks/useCustomToast"
import {
  formatLodgeStayRange,
  getCamperLunchLabels,
} from "@/lib/booking-mappers"
import { parseLocalDate } from "@/lib/utils"

export const Route = createFileRoute("/_layout/bookings/$id/")({
  component: BookingDetailPage,
  staticData: {
    pageTitle: "Booking Details",
    pageIcon: Calendar,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "Booking Details - Broken Islands Booking",
      },
    ],
  }),
})

/**
 * Map a booking status string to the visual badge variant used for styling.
 *
 * @param status - Booking status (e.g., "confirmed", "pending", "cancelled", "completed")
 * @returns The badge variant to use: `'default'` for confirmed, `'secondary'` for pending or unknown statuses, `'destructive'` for cancelled, and `'outline'` for completed
 */
function getStatusBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "confirmed":
      return "default"
    case "pending":
      return "secondary"
    case "cancelled":
      return "destructive"
    case "completed":
      return "outline"
    default:
      return "secondary"
  }
}

/**
 * Convert an internal booking type identifier into a human-readable label.
 *
 * @param type - Booking type identifier (e.g., `"lodge"`, `"camping"`, `"mixed"`)
 * @returns The display label for the booking type (`"Lodge Stay"`, `"Camping"`, `"Mixed Stay"`) or the original `type` if unknown
 */
function getBookingTypeLabel(type: string): string {
  switch (type) {
    case "lodge":
      return "Lodge Stay"
    case "camping":
      return "Camping"
    case "mixed":
      return "Mixed Stay"
    default:
      return type
  }
}

/**
 * Renders a skeleton placeholder for the Booking Details page while data is loading.
 *
 * @returns A JSX element showing placeholder header and content cards for booking details.
 */
function BookingDetailSkeleton() {
  return (
    <div className="container max-w-4xl py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-9" />
        <Skeleton className="h-8 w-64" />
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-56" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Renders a "Booking Not Found" view that informs the user the booking is missing and provides a way to return to the bookings list.
 *
 * @returns A JSX element displaying a not-found card with a message and a button that navigates back to the bookings listing.
 */
function BookingNotFound() {
  const router = useRouter()

  return (
    <div className="container max-w-4xl py-6">
      <Card>
        <CardContent className="p-6 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The booking you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => router.history.push("/bookings")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Bookings
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Renders a card displaying guest contact and stay information for a booking.
 *
 * Shows primary contact name, email, optional phone, number of guests, booking type,
 * stay dates, and every lodge portion for mixed bookings. Legacy singular
 * lodge dates remain supported when no lodge-stay array is present.
 *
 * @param booking - Booking details used to populate the guest information fields.
 * @returns A Card element containing the guest information section.
 */
function GuestSection({ booking }: { booking: BookingDetailResponse }) {
  const lodgeStays =
    booking.lodge_stays && booking.lodge_stays.length > 0
      ? booking.lodge_stays
      : booking.lodge_start_date && booking.lodge_end_date
        ? [
            {
              start_date: booking.lodge_start_date,
              end_date: booking.lodge_end_date,
            },
          ]
        : []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Guest Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Primary Contact</p>
            <p className="font-medium">{booking.guest.primary_contact_name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Email</p>
            <p className="font-medium flex items-center gap-1">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {booking.guest.email}
            </p>
          </div>
          {booking.guest.phone && (
            <div>
              <p className="text-sm text-muted-foreground">Phone</p>
              <p className="font-medium flex items-center gap-1">
                <Phone className="h-4 w-4 text-muted-foreground" />
                {booking.guest.phone}
              </p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground">Number of Guests</p>
            <p className="font-medium flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              {booking.number_of_guests} guest
              {booking.number_of_guests !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <Separator />

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Booking Type</p>
            <Badge variant="outline" className="mt-1">
              {getBookingTypeLabel(booking.booking_type)}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Stay Dates</p>
            <p className="font-medium flex items-center gap-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {format(parseLocalDate(booking.arrival_date), "MMM d, yyyy")} -{" "}
              {format(parseLocalDate(booking.departure_date), "MMM d, yyyy")}
            </p>
          </div>
          {booking.booking_type === "mixed" && lodgeStays.length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-sm text-muted-foreground">Lodge Portions</p>
              <div className="space-y-1 font-medium">
                {lodgeStays.map((stay, index) => (
                  <p key={`${stay.start_date}-${stay.end_date}-${index}`}>
                    {formatLodgeStayRange(stay)}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Render the "Water Taxi Trips" card for a booking, listing each scheduled trip.
 *
 * @param booking - Booking detail object used to display its water taxi trips
 * @returns The section as a Card element showing each trip's type, time, passenger and kayak counts, pickup/dropoff locations, and notes; `null` when the booking has no water taxi trips.
 */
function WaterTaxiSection({ booking }: { booking: BookingDetailResponse }) {
  if (booking.water_taxi_trips.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ship className="h-5 w-5" />
          Water Taxi Trips
          <Badge variant="secondary" className="ml-2">
            {booking.water_taxi_trips.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {booking.water_taxi_trips.map((trip, index) => (
            <div key={trip.id} className="rounded-lg border p-4 bg-muted/30">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <Badge variant="outline" className="capitalize">
                    {trip.trip_type}
                  </Badge>
                </div>
                <span className="text-sm text-muted-foreground">
                  Trip #{index + 1}
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Time:</span>{" "}
                  <span className="font-medium">
                    {trip.scheduled_time || trip.custom_time || "Not specified"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Passengers:</span>{" "}
                  <span className="font-medium">{trip.passenger_count}</span>
                </div>
                {trip.kayak_count > 0 && (
                  <div>
                    <span className="text-muted-foreground">Kayaks:</span>{" "}
                    <span className="font-medium">{trip.kayak_count}</span>
                  </div>
                )}
                {trip.pickup_location && (
                  <div>
                    <span className="text-muted-foreground">Pickup:</span>{" "}
                    <span className="font-medium">{trip.pickup_location}</span>
                  </div>
                )}
                {trip.dropoff_location && (
                  <div>
                    <span className="text-muted-foreground">Dropoff:</span>{" "}
                    <span className="font-medium">{trip.dropoff_location}</span>
                  </div>
                )}
              </div>

              {trip.notes && (
                <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                  {trip.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Render the Kayak Rentals section for a booking.
 *
 * Shows a card listing each kayak rental with guest name, equipment type/size,
 * pickup and return dates/times, and optional notes. The section is omitted
 * when the booking has no kayak rentals.
 *
 * @param booking - The booking whose `kayak_rentals` will be displayed
 * @returns A card element containing the kayak rentals list, or `null` if there are none
 */
function KayakRentalsSection({ booking }: { booking: BookingDetailResponse }) {
  if (booking.kayak_rentals.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-label="Kayak icon"
          >
            <title>Kayak</title>
            <path d="M2 12h20M4 12c0-2 2-4 8-4s8 2 8 4M4 12c0 2 2 4 8 4s8-2 8-4" />
            <path d="M12 8v8" />
          </svg>
          Kayak Rentals
          <Badge variant="secondary" className="ml-2">
            {booking.kayak_rentals.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {booking.kayak_rentals.map((rental) => (
            <div key={rental.id} className="rounded-lg border p-4 bg-muted/30">
              <div className="flex items-start justify-between mb-3">
                <p className="font-medium">{rental.guest_name}</p>
                <Badge variant="outline" className="capitalize">
                  {rental.equipment_type} / {rental.equipment_size}
                </Badge>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Pickup:</span>{" "}
                  <span className="font-medium">
                    {format(parseLocalDate(rental.pickup_date), "MMM d, yyyy")}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Return:</span>{" "}
                  <span className="font-medium">
                    {format(parseLocalDate(rental.return_date), "MMM d, yyyy")}
                  </span>
                </div>
              </div>

              {rental.notes && (
                <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                  {rental.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Render the Dietary Requirements card for a booking when applicable.
 *
 * @param booking - Booking details used to display per-guest dietary requirements
 * @returns The Dietary Requirements Card element, or `null` if the booking has no dietary requirements
 */
function DietarySection({ booking }: { booking: BookingDetailResponse }) {
  if (booking.dietary_requirements.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Utensils className="h-5 w-5" />
          Dietary Requirements
          <Badge variant="secondary" className="ml-2">
            {booking.dietary_requirements.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {booking.dietary_requirements.map((dietary) => (
            <div key={dietary.id} className="rounded-lg border p-4 bg-muted/30">
              <div className="flex items-start justify-between mb-3">
                <p className="font-medium">{dietary.guest_name}</p>
                <Badge
                  variant={
                    dietary.dietary_type === "none" ? "outline" : "secondary"
                  }
                  className="capitalize"
                >
                  {dietary.dietary_type === "none"
                    ? "No restrictions"
                    : dietary.dietary_type.replace("-", " ")}
                </Badge>
              </div>

              {booking.booking_type === "camping" ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {getCamperLunchLabels({
                    camper_lunches: {
                      arrival: dietary.camper_lunches?.arrival ?? false,
                      departure: dietary.camper_lunches?.departure ?? false,
                    },
                  }).map((label) => (
                    <Badge key={label} variant="outline" className="text-xs">
                      {label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                  {dietary.breakfast && (
                    <Badge variant="outline" className="text-xs">
                      Breakfast
                    </Badge>
                  )}
                  {dietary.lunch && (
                    <Badge variant="outline" className="text-xs">
                      Lunch
                    </Badge>
                  )}
                  {dietary.dinner && (
                    <Badge variant="outline" className="text-xs">
                      Dinner
                    </Badge>
                  )}
                </div>
              )}

              {dietary.allergies && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Allergies:</span>{" "}
                  <span
                    className={
                      dietary.allergy_severity === "severe"
                        ? "text-destructive font-medium"
                        : "font-medium"
                    }
                  >
                    {dietary.allergies}
                    {dietary.allergy_severity && (
                      <span className="ml-1">({dietary.allergy_severity})</span>
                    )}
                  </span>
                </div>
              )}

              {dietary.notes && (
                <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                  {dietary.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Displays detailed information for a single booking and provides edit and cancel actions.
 *
 * Renders booking metadata, guest information, water taxi trips, kayak rentals, and dietary requirements.
 * Confirming cancellation cancels the booking, invalidates related queries, shows success/error toasts, and navigates back to the bookings list.
 *
 * @returns The Booking Detail page as a React element.
 */
function BookingDetailPage() {
  const router = useRouter()
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const {
    data: booking,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["booking", id],
    queryFn: () => BookingsService.getBooking({ bookingId: id }),
  })

  const cancelMutation = useMutation({
    mutationFn: () => BookingsService.cancelBooking({ bookingId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] })
      queryClient.invalidateQueries({ queryKey: ["booking", id] })
      showSuccessToast("Booking cancelled successfully")
      router.navigate({ to: "/bookings" })
    },
    onError: () => {
      showErrorToast("Failed to cancel booking")
    },
  })

  const handleCancelBooking = () => {
    setShowCancelDialog(false)
    cancelMutation.mutate()
  }

  if (isLoading) {
    return <BookingDetailSkeleton />
  }

  if (isError || !booking) {
    return <BookingNotFound />
  }

  const isCancelled = booking.status === "cancelled"

  return (
    <div className="container max-w-4xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to bookings"
            onClick={() => router.history.push("/bookings")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">
              {booking.guest.primary_contact_name}
            </h1>
            <p className="text-sm text-muted-foreground">
              #{booking.id.slice(0, 8)} | Created{" "}
              {format(new Date(booking.created_at), "MMM d, yyyy")}
              {booking.cancelled_at && (
                <>
                  {" "}
                  | Cancelled{" "}
                  {format(new Date(booking.cancelled_at), "MMM d, yyyy")}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={getStatusBadgeVariant(booking.status)}
            className="capitalize"
          >
            {booking.status}
          </Badge>
        </div>
      </div>

      {/* Action Buttons - only show for non-cancelled bookings */}
      {!isCancelled && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              router.navigate({ to: "/bookings/$id/edit", params: { id } })
            }
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Booking
          </Button>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowCancelDialog(true)}
            disabled={cancelMutation.isPending}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Cancel Booking
          </Button>
        </div>
      )}

      {/* Sections */}
      <GuestSection booking={booking} />
      <WaterTaxiSection booking={booking} />
      <KayakRentalsSection booking={booking} />
      <DietarySection booking={booking} />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this booking for{" "}
              <strong>{booking.guest.primary_contact_name}</strong>? This action
              cannot be undone. The booking will be marked as cancelled and
              removed from calendar views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
